import React, { useState, useEffect, useRef } from 'react';
import {
   Grid,
   GridItem,
   FormControl,
   Input,
   useColorModeValue,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Button,
   Checkbox,
   useToast,
   Select,
   useColorMode,
} from '@chakra-ui/react';

const UnitMasterForm = ({ isOpen, onClose, initialData, isEditMode, fetchData }) => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   const [currentRow, setCurrentRow] = useState(
      initialData || { name: '', code: '', status: true, type: '', format: '' },
   );
   const [loading, setLoading] = useState(false);
   const textColor = useColorModeValue('#9E9E9E', 'white');
   const [selectedValue, setSelectedValue] = useState('');
   const toast = useToast();

   React.useEffect(() => {
      if (initialData) {
         setCurrentRow(initialData);
      }
   }, [initialData]);

   const handleSave = async () => {
      const url = isEditMode ? '/uqc/update' : '/uqc/add';
      const accessToken = JSON.parse(localStorage.getItem('authTokenCustomer'))?.access_token;

      setLoading(true);

      try {
         const response = await fetch(`${process.env.REACT_APP_BASEURL}${url}`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(currentRow),
         });

         const result = await response.json();

         if (result?.code === 1) {
            toast({
               title: isEditMode ? 'Unit updated successfully!' : 'Unit added successfully!',
               status: 'success',
               duration: 3000,
               isClosable: true,
            });
            fetchData();
            onClose();
         } else {
            toast({
               title: 'Operation failed',
               description: result?.message || 'An error occurred',
               status: 'error',
               duration: 3000,
               isClosable: true,
            });
         }
      } catch (error) {
         toast({
            title: 'Error',
            description: 'Failed to save the unit. Please try again later.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         console.error('Error saving data:', error);
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      const handleKeyDown = (e) => {
         const activeElement = document.activeElement;
         const isTyping =
            activeElement?.tagName === 'INPUT' ||
            activeElement?.tagName === 'TEXTAREA' ||
            activeElement?.getAttribute('role') === 'textbox' ||
            activeElement?.isContentEditable;

         if (isTyping) return;

         if (e.key.toLowerCase() === 's') {
            e.preventDefault();

            if (e.shiftKey) {
               handlesavecontinue();
            } else {
               handleSave();
            }
         }
         if (e.key.toLowerCase() === 'c') {
            onClose();
         }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
         window.removeEventListener('keydown', handleKeyDown);
      };
   }, []);

   const options = [
      {
         label: 'Kilo',
         value: 'kilo',
      },
      {
         label: 'Area',
         value: 'area',
      },
      {
         label: 'Length',
         value: 'length',
      },
      {
         label: 'Volume',
         value: 'volume',
      },
      {
         label: 'Weight',
         value: 'weight',
      },
      {
         label: 'Measure',
         value: 'measure',
      },
      {
         label: 'Other',
         value: 'other',
      },
   ];

   const handlesavecontinue = async () => {
      const url = isEditMode ? '/uqc/update' : '/uqc/add';
      const accessToken = JSON.parse(localStorage.getItem('authTokenCustomer'))?.access_token;

      setLoading(true);

      try {
         const response = await fetch(`${process.env.REACT_APP_ADMIN_URL}${url}`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(currentRow),
         });

         const result = await response.json();

         if (result?.code === 1) {
            toast({
               title: isEditMode ? 'Unit updated successfully!' : 'Unit added successfully!',
               status: 'success',
               duration: 3000,
               isClosable: true,
            });
            fetchData();
         } else {
            toast({
               title: 'Operation failed',
               description: result?.message || 'An error occurred',
               status: 'error',
               duration: 3000,
               isClosable: true,
            });
         }
      } catch (error) {
         toast({
            title: 'Error',
            description: 'Failed to save the unit. Please try again later.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         console.error('Error saving data:', error);
      } finally {
         setLoading(false);
      }
   };

   const nameInputRef = useRef();
   const codeRef = useRef();
   const formatRef = useRef();
   const typeRef = useRef();
   const boxRef = useRef();
   const saveFinishRef = useRef();
   const cancelRef = useRef();

   useEffect(() => {
      setTimeout(() => {
         nameInputRef.current?.focus();
      }, 100);
   }, [isOpen]);

   return (
      <Modal isOpen={isOpen} onClose={onClose} size="3xl">
         <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
         <ModalContent bg="transparent" boxShadow="none" mx="15px" mt={{ base: '45%', md: '25%', xl: '15%' }}>
            <div
               className={`rounded-2xl shadow-xl overflow-hidden
               ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}
            `}>
               {/* Colored top border */}
               <div className="h-1 bg-gradient-to-r from-purple-500 via-blue-500 to-indigo-500" />

               {/* Header */}
               <ModalHeader className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                     <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-blue-500 rounded-full" />
                     <div>
                        <h2 className={`text-xl font-semibold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                           {isEditMode ? 'Edit Unit' : 'Add New Unit'}
                        </h2>
                        <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                           {isEditMode ? 'Update unit details' : 'Create a new unit for your organization'}
                        </p>
                     </div>
                  </div>
               </ModalHeader>

               <ModalCloseButton
                  className={`top-5 right-5 rounded-md ${isDark
                     ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                     : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                     }`}
                  _focus={{
                     boxShadow: '0 0 0 2px rgba(99, 102, 241, 0.5)',
                     outline: 'none',
                  }}
               />

               {/* Body */}
               <ModalBody className="px-6 py-5">
                  <Grid
                     templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', xl: 'repeat(2, 1fr)' }}
                     gap={4}>
                     {/* Name Field */}
                     <GridItem>
                        <FormControl>
                           <label
                              className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'
                                 }`}>
                              Name
                           </label>
                           <Input
                              id="name"
                              ref={nameInputRef}
                              placeholder="Enter name"
                              value={currentRow.name || ''}
                              onChange={(e) => setCurrentRow({ ...currentRow, name: e.target.value })}
                              onKeyDown={(e) => {
                                 if (e.key === 'Enter' || e.key === 'Tab') {
                                    e.preventDefault();
                                    codeRef.current?.focus();
                                 }
                              }}
                              className={`w-full px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 
                                 ${isDark
                                    ? 'bg-gray-700 border border-gray-600 text-gray-100 placeholder-gray-500 focus:border-blue-500'
                                    : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                                 }
                                 focus:outline-none focus:ring-2 ${isDark ? 'focus:ring-blue-500/20' : 'focus:ring-blue-500/10'
                                 }
                              `}
                           />
                        </FormControl>
                     </GridItem>

                     {/* Code Field */}
                     <GridItem>
                        <FormControl>
                           <label
                              className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'
                                 }`}>
                              Code
                           </label>
                           <Input
                              id="code"
                              ref={codeRef}
                              placeholder="Enter code"
                              value={currentRow.code || ''}
                              onChange={(e) => setCurrentRow({ ...currentRow, code: e.target.value })}
                              onKeyDown={(e) => {
                                 if (e.key === 'Enter' || e.key === 'Tab') {
                                    e.preventDefault();
                                    formatRef.current?.focus();
                                 }
                              }}
                              className={`w-full px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 
                                 ${isDark
                                    ? 'bg-gray-700 border border-gray-600 text-gray-100 placeholder-gray-500 focus:border-blue-500'
                                    : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                                 }
                                 focus:outline-none focus:ring-2 ${isDark ? 'focus:ring-blue-500/20' : 'focus:ring-blue-500/10'
                                 }
                              `}
                           />
                        </FormControl>
                     </GridItem>

                     {/* Format Field */}
                     <GridItem>
                        <FormControl>
                           <label
                              className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'
                                 }`}>
                              Format
                           </label>
                           <Input
                              id="format"
                              ref={formatRef}
                              placeholder="Enter format"
                              value={currentRow.format || ''}
                              onChange={(e) => setCurrentRow({ ...currentRow, format: e.target.value })}
                              onKeyDown={(e) => {
                                 if (e.key === 'Enter' || e.key === 'Tab') {
                                    e.preventDefault();
                                    typeRef.current?.focus();
                                 }
                              }}
                              className={`w-full px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 
                                 ${isDark
                                    ? 'bg-gray-700 border border-gray-600 text-gray-100 placeholder-gray-500 focus:border-blue-500'
                                    : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                                 }
                                 focus:outline-none focus:ring-2 ${isDark ? 'focus:ring-blue-500/20' : 'focus:ring-blue-500/10'
                                 }
                              `}
                           />
                        </FormControl>
                     </GridItem>

                     {/* Type Field */}
                     <GridItem>
                        <FormControl>
                           <label
                              className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'
                                 }`}>
                              Type
                           </label>
                           <div className="relative">
                              <Select
                                 ref={typeRef}
                                 placeholder="Select option"
                                 value={currentRow.type}
                                 onChange={(e) => setCurrentRow({ ...currentRow, type: e.target.value })}
                                 onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === 'Tab') {
                                       e.preventDefault();
                                       boxRef.current?.focus();
                                    }
                                 }}
                                 className={`w-full px-3.5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 appearance-none cursor-pointer
                                    ${isDark
                                       ? 'bg-gray-700 border border-gray-600 text-gray-100 focus:border-blue-500'
                                       : 'bg-white border border-gray-300 text-gray-900 focus:border-blue-500'
                                    }
                                    focus:outline-none focus:ring-2 ${isDark ? 'focus:ring-blue-500/20' : 'focus:ring-blue-500/10'
                                    }
                                 `}>
                                 {options.map((option) => (
                                    <option key={option.value} value={option.value}>
                                       {option.label}
                                    </option>
                                 ))}
                              </Select>
                              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                 <svg
                                    className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth={2}
                                       d="M19 9l-7 7-7-7"
                                    />
                                 </svg>
                              </div>
                           </div>
                        </FormControl>
                     </GridItem>

                     {/* Status Field */}
                     <GridItem>
                        <label
                           className={`block text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                           Status
                        </label>
                        <div className="pt-2">
                           <Checkbox
                              isChecked={currentRow.status}
                              ref={boxRef}
                              onChange={(e) => setCurrentRow({ ...currentRow, status: e.target.checked })}
                              onKeyDown={(e) => {
                                 if (e.key === 'Enter' || e.key === 'Tab') {
                                    e.preventDefault();
                                    saveFinishRef.current?.focus();
                                 }
                              }}
                              className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                              colorScheme="blue">
                              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                 Inactive
                              </span>
                           </Checkbox>
                        </div>
                     </GridItem>
                  </Grid>
               </ModalBody>

               {/* Footer */}
               <ModalFooter
                  className={`px-6 py-4 border-t flex flex-wrap gap-3 justify-end ${isDark
                        ? 'border-gray-700/50 bg-gradient-to-r from-gray-800/50 to-gray-900/50'
                        : 'border-gray-200 bg-gradient-to-r from-gray-50 to-white'
                     }`}>
                  <Button
                     onClick={handleSave}
                     isLoading={loading}
                     ref={saveFinishRef}
                     loadingText="Saving"
                     size="sm"
                     className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5
      ${isDark
                           ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-500 hover:to-blue-400 border border-blue-400/50'
                           : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 border-2 border-blue-400/30'
                        }
    `}
                     _focus={{
                        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.4)',
                        outline: 'none',
                     }}
                     onKeyDown={(e) => {
                        if (e.key === 'ArrowRight') {
                           e.preventDefault();
                           cancelRef.current?.focus();
                        }
                     }}>
                     <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                           />
                        </svg>
                        Save & Finish
                     </span>
                  </Button>

                  <Button
                     ref={cancelRef}
                     onClick={onClose}
                     size="sm"
                     className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5
      ${isDark
                           ? 'bg-gradient-to-r from-red-600/80 to-red-500/80 text-white hover:from-red-500 hover:to-red-400 border border-red-400/50'
                           : 'bg-white text-red-600 hover:bg-red-50 border-2 border-red-300 hover:border-red-400'
                        }
    `}
                     _focus={{
                        boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.3)',
                        outline: 'none',
                     }}
                     onKeyDown={(e) => {
                        if (e.key === 'ArrowLeft') {
                           e.preventDefault();
                           saveFinishRef.current?.focus();
                        }
                     }}>
                     <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                           />
                        </svg>
                        Cancel
                     </span>
                  </Button>
               </ModalFooter>
            </div>
         </ModalContent>
      </Modal>
   );
};

export default UnitMasterForm;
